import React, { useState } from "react";
import Icon from "../../../components/AppIcon";
import OrderStatusBadge from "./OrderStatusBadge";
import FiltersBar from "./FiltersBar";
import OrderDetailsModal from "./OrderDetailsModal";
import DeleteOrderModal from "./DeleteOrderModal";
import { useTranslation } from "react-i18next";
import { useDispatch } from "react-redux";
import {
  deleteOrderService,
  getOrdersService,
} from "redux/services/orders_service";
import { toast } from "react-toastify";

const OrderTable = ({ orders }) => {
  const { t, i18n } = useTranslation();
  const dispatch = useDispatch();

  const [selectedOrderDetails, setSelectedOrderDetails] = useState(null);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [orderToDelete, setOrderToDelete] = useState(null);

  const formatCurrency = (amount) =>
    new Intl.NumberFormat(i18n.language === "en" ? "en" : "ar", {
      style: "currency",
      currency: "IQD",
    }).format(amount);

  const formatDate = (date) =>
    new Date(date).toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
      year: "numeric",
    });

  const handleDeleteClick = (order) => {
    setOrderToDelete(order);
    setIsDeleteModalOpen(true);
  };

  const handleConfirmDelete = (orderId) => {
    dispatch(deleteOrderService(orderId)).then((res) => {
      if (res?.payload?.status === 200 || res?.payload?.status === 204) {
        toast("تم الحذف بنجاح");
        dispatch(getOrdersService({ page: 1 }));
        setIsDeleteModalOpen(false);
      }
    });
  };

  /* ========= FIELDS (ONE SOURCE OF TRUTH) ========= */
  const getOrderFields = ({ order, variant = "table" }) => {
    const isMobile = variant === "mobile";

    return {
      orderNumber: (
        <span className="font-mono text-sm font-medium">
          #{order.order_number}
        </span>
      ),

      customer: (
        <div className="flex items-center gap-3">
          <div className="w-8 h-8 bg-primary/10 rounded-full flex items-center justify-center">
            <Icon name="User" size={16} className="text-primary" />
          </div>
          <div>
            <div className="font-medium">
              {order.first_name} {order.last_name}
            </div>
            <div className="text-xs text-muted-foreground">
              {order.email}
            </div>
          </div>
        </div>
      ),

      date: (
        <div>
          <div className="text-sm">{formatDate(order.created_at)}</div>
          <div className="text-xs text-muted-foreground">
            {new Date(order.created_at).toLocaleTimeString("en-US", {
              hour: "2-digit",
              minute: "2-digit",
            })}
          </div>
        </div>
      ),

      status: (
        <OrderStatusBadge
          status={order.status}
          size={isMobile ? "sm" : "md"}
        />
      ),

      items: (
        <div>
          <div className="text-sm">
            {order.itemCount} {t("items")}
          </div>
          <div className="text-xs text-muted-foreground truncate">
            {order.products
              .slice(0, 2)
              .map((p) => p.name)
              .join(", ")}
            {order.products.length > 2 &&
              ` +${order.products.length - 2}`}
          </div>
        </div>
      ),

      total: (
        <div>
          <div className="font-semibold">
            {formatCurrency(order.total_price)}
          </div>
          <div className="text-xs text-muted-foreground">
            {order.paymentMethod}
          </div>
        </div>
      ),

      actions: (
        <div className="flex gap-2">
          <button onClick={() => setSelectedOrderDetails(order)}>
            <Icon name="Eye" size={16} />
          </button>
          <button
            onClick={() => handleDeleteClick(order)}
            className="text-error"
          >
            <Icon name="Trash2" size={16} />
          </button>
        </div>
      ),
    };
  };

  return (
    <>
      {/* ================= DESKTOP TABLE ================= */}
      <div className="hidden lg:block bg-white rounded-md p-4 border">
        <FiltersBar />
        <table className="w-full text-sm">
          <thead className="bg-green-50">
            <tr>
              <th className="p-3">{t("orderNumber")}</th>
              <th className="p-3">{t("customer")}</th>
              <th className="p-3">{t("date")}</th>
              <th className="p-3">{t("status")}</th>
              <th className="p-3">{t("items")}</th>
              <th className="p-3">{t("total")}</th>
              <th className="p-3">{t("actions")}</th>
            </tr>
          </thead>

          <tbody className="divide-y">
            {orders.map((order) => {
              const f = getOrderFields({ order });
              return (
                <tr key={order.id} className="hover:bg-muted/50">
                  <td className="p-3">{f.orderNumber}</td>
                  <td className="p-3">{f.customer}</td>
                  <td className="p-3">{f.date}</td>
                  <td className="p-3">{f.status}</td>
                  <td className="p-3">{f.items}</td>
                  <td className="p-3">{f.total}</td>
                  <td className="p-3">{f.actions}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* ================= MOBILE CARDS ================= */}
      <div className="lg:hidden space-y-4">
        {orders.map((order) => {
          const f = getOrderFields({ order, variant: "mobile" });

          return (
            <div
              key={order.id}
              className="bg-white border rounded-lg p-4 space-y-3"
            >
              <div className="flex justify-between items-center">
                {f.orderNumber}
                {f.status}
              </div>

              {f.customer}
              {f.date}
              {f.items}

              <div className="flex justify-between items-center">
                {f.total}
                {f.actions}
              </div>
            </div>
          );
        })}
      </div>

      {selectedOrderDetails && (
        <OrderDetailsModal
          order={selectedOrderDetails}
          onClose={() => setSelectedOrderDetails(null)}
        />
      )}

      <DeleteOrderModal
        isOpen={isDeleteModalOpen}
        order={orderToDelete}
        language={i18n.language}
        onClose={() => setIsDeleteModalOpen(false)}
        onConfirm={handleConfirmDelete}
      />
    </>
  );
};

export default OrderTable;
